/**
 * Changing a map's BPM can keep its objects on the same beats (the default) or at the same times.
 * Keeping them on their beats means every time-valued field moves by oldBpm / newBpm, while every
 * `time_beats` stays put — a beat is the same beat at any tempo. Pure functions only; the store holds the
 * state and calls these.
 */
import type { Difficulty, Note, Obstacle, Slide } from '../types/beatmap';

/** Whether any difficulty holds an object whose time depends on the BPM (lights and bookmarks are
 *  stored in beats and never move). */
export function hasTimedObjects(difficulties: Record<string, Difficulty>): boolean {
  return Object.values(difficulties).some(d =>
    d.notes.length || (d.slides?.length ?? 0) || (d.crouches?.length ?? 0) || (d.jumps?.length ?? 0)
    || (d.squares?.length ?? 0) || (d.triangles?.length ?? 0) || (d.crouch_obstacles?.length ?? 0));
}

type Timed = { time_ms: number; time_seconds: number; position?: number[] | null };

/** A wall's or note's position carries its encoded time as the third component (ms × 0.02). */
const scaleZ = (position: number[], k: number): number[] =>
  position.length >= 3 ? [position[0], position[1], position[2] * k, ...position.slice(3)] : position;

function scaleTimed<T extends Timed>(o: T, k: number): T {
  const out = { ...o, time_ms: o.time_ms * k, time_seconds: o.time_seconds * k };
  if (o.position) out.position = scaleZ(o.position, k);
  return out;
}

function scaleNote(n: Note, k: number): Note {
  return {
    ...n,
    time_ms: n.time_ms * k,
    time_seconds: n.time_seconds * k,
    position: scaleZ(n.position, k) as [number, number, number],
    rail_nodes: n.rail_nodes ? n.rail_nodes.map(([x, y, z]) => [x, y, z * k] as [number, number, number]) : n.rail_nodes,
  };
}

/** One difficulty with every object kept on its beat at `newBpm`. */
export function rescaleDifficulty(d: Difficulty, oldBpm: number, newBpm: number): Difficulty {
  const k = oldBpm / newBpm;
  const out: Difficulty = {
    ...d,
    notes: d.notes.map(n => scaleNote(n, k)),
    slides: (d.slides ?? []).map((s: Slide) => scaleTimed(s, k)),
    crouches: (d.crouches ?? []).map(t => t * k),
    jumps: (d.jumps ?? []).map(t => t * k),
    duration_seconds: d.duration_seconds * k,
  };
  if (d.squares) out.squares = d.squares.map((o: Obstacle) => scaleTimed(o, k));
  if (d.triangles) out.triangles = d.triangles.map((o: Obstacle) => scaleTimed(o, k));
  if (d.crouch_obstacles) out.crouch_obstacles = d.crouch_obstacles.map((o: Obstacle) => scaleTimed(o, k));
  return out;
}

/**
 * One difficulty with every object kept at its time at `newBpm`: `time_ms` stays and each `time_beats` is
 * re-derived from it, so an object that was on a beat now stands wherever its time falls between beats.
 * (A rail node's beat is decoded from its encoded time, so it follows without a change here.)
 */
export function rebeatDifficulty(d: Difficulty, newBpm: number): Difficulty {
  const beat = (ms: number) => (ms / 1000) * (newBpm / 60);
  const out: Difficulty = {
    ...d,
    notes: d.notes.map(n => ({ ...n, time_beats: beat(n.time_ms) })),
    slides: (d.slides ?? []).map(s => ({ ...s, time_beats: beat(s.time_ms) })),
    duration_beats: d.duration_seconds * (newBpm / 60),
  };
  if (d.squares) out.squares = d.squares.map(o => ({ ...o, time_beats: beat(o.time_ms) }));
  if (d.triangles) out.triangles = d.triangles.map(o => ({ ...o, time_beats: beat(o.time_ms) }));
  if (d.crouch_obstacles) out.crouch_obstacles = d.crouch_obstacles.map(o => ({ ...o, time_beats: beat(o.time_ms) }));
  return out;
}

export function rebeatDifficulties(difficulties: Record<string, Difficulty>, newBpm: number): Record<string, Difficulty> {
  return Object.fromEntries(Object.entries(difficulties).map(([name, d]) => [name, rebeatDifficulty(d, newBpm)]));
}

export function rescaleDifficulties(
  difficulties: Record<string, Difficulty>, oldBpm: number, newBpm: number,
): Record<string, Difficulty> {
  return Object.fromEntries(Object.entries(difficulties).map(([name, d]) => [name, rescaleDifficulty(d, oldBpm, newBpm)]));
}
